/* eslint-disable no-console */
/* eslint-disable react/prop-types */
import React, { createContext, useMemo, useState, FC } from 'react';
import { useMutation, ExecutionResult } from 'react-apollo';

import submitSurveyResponse from './SubmitResponses.graphql';

import {
  GetCurrentSurveySessionQuery,
  SubmitSurveyResponseMutation,
  SubmitSurveyResponseMutationVariables,
  QuestionAnswerInput,
} from '~lib/generatedGqlTypes';

type Survey = GetCurrentSurveySessionQuery['surveyTakingSession']['survey'];
type ActiveSurveyPage = GetCurrentSurveySessionQuery['surveyTakingSession']['currentPage'];

type AnswersFromContext = {
  questionId: string;
  value: string;
};
type SurveyContextType = {
  survey?: Survey;
  activePage?: ActiveSurveyPage;
  nextPage(): void;
  previousPage(): void;
  submitSurvey(): Promise<ExecutionResult<SubmitSurveyResponseMutation>>;
};
const DEFAULT_PROMISE_RESOLVER = {
  data: {
    submitSurveyResponse: {
      success: false,
      userErrors: null,
      respondent: null,
    },
  },
};

export const SurveyContext = createContext<SurveyContextType>({
  survey: undefined,
  activePage: undefined,
  nextPage: () => {},
  previousPage: () => {},
  submitSurvey: async () => {
    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  },
});

export type SurveyProviderProps = {
  children: React.ReactNode;
  survey: Survey;
  currentPage: GetCurrentSurveySessionQuery['surveyTakingSession']['currentPage'];
  collector: GetCurrentSurveySessionQuery['surveyTakingSession']['collector'];
};

const SurveyProvider: FC<SurveyProviderProps> = ({ children, survey: initialSurvey, currentPage, collector }) => {
  // Survey only rendering first page until we have information about who is responsible for providing next page.
  // When we get more information about how we will be acquiring other pages we will add those mechanisms here.
  const activePage = useMemo(() => currentPage, [currentPage]);
  // Survey currently holds error state, setSurvey will need to be called when upstream actions are hooked up.
  const [survey] = useState(initialSurvey);

  // TODO: https://jira.surveymonkey.com/browse/SPAGE-7197
  const answers: AnswersFromContext[] = [
    {
      questionId: '348412277',
      value: 'This is test data for Q1',
    },
    {
      questionId: '349335593',
      value: 'This is test data for Q10',
    },
  ];

  const responses: QuestionAnswerInput[] = answers.map(answer => {
    const { questionId, value } = answer;

    return {
      questionId,
      pageId: currentPage?.id ?? '',
      openEnded: [{ textAnswer: value, fieldSetId: '' }],
      choice: [],
      implicit: [],
    };
  });

  const [submitCurrentResponses] = useMutation<SubmitSurveyResponseMutation, SubmitSurveyResponseMutationVariables>(
    submitSurveyResponse,
    {
      variables: {
        surveyId: survey?.id ?? '',
        input: {
          collectorId: collector?.id ?? '',
          surveyVersion: survey?.version ?? 1,
          dateStarted: new Date().toISOString(),
          respondentId: '',
          responses,
        },
      },
    }
  );

  const handlePostResponseSubmission = (): void => {
    console.log('This is where post submission redirect logic would live');
  };

  const nextPage = (): void => {
    console.log('Use this method to get the next page.  Pending decision about pagination');
  };
  const previousPage = (): void => {
    console.log('Use this method to get the previous page.  Pending decision about pagination');
  };

  const submitSurvey = async (): Promise<ExecutionResult<SubmitSurveyResponseMutation>> => {
    if (survey) {
      const result = submitCurrentResponses();
      await result
        .then(data => {
          console.log(data);
          handlePostResponseSubmission();
        })
        .catch(error => {
          console.log(error);
        });

      return result;
    }

    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  };

  return (
    <SurveyContext.Provider
      value={{
        survey,
        activePage,
        nextPage,
        previousPage,
        submitSurvey,
      }}
    >
      <>{children}</>
    </SurveyContext.Provider>
  );
};

export default SurveyProvider;
